import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Star, CalendarCheck } from "lucide-react";
import { NAV_LINKS, waLink } from "../lib/constants";
import { useSite } from "../context/SiteContext";

export default function Navbar() {
  const { site } = useSite();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-black/80 to-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          {site?.logo_barbearia ? (
            <img src={site.logo_barbearia} alt="Barbearia das Estrelas" className="h-12 lg:h-14 w-auto object-contain" />
          ) : (
            <>
              <Star className="w-5 h-5 text-gold fill-gold" />
              <span className="font-heading font-600 tracking-tight text-white text-base lg:text-lg leading-none">
                BARBEARIA <span className="text-gold">DAS ESTRELAS</span>
              </span>
            </>
          )}
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              className={`text-sm font-500 tracking-wide transition-colors ${
                location.pathname === l.to ? "text-gold" : "text-zinc-300 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={waLink(site, "barbearia")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-agendar-agora"
            className="hidden sm:inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-black font-heading font-600 text-sm tracking-wide px-5 py-2.5 rounded-sm transition-colors"
          >
            <CalendarCheck className="w-4 h-4" />
            AGENDAR AGORA
          </a>
          <button
            className="xl:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-black/95 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`py-3 border-b border-white/5 font-heading tracking-wide text-lg ${
                  location.pathname === l.to ? "text-gold" : "text-zinc-200"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <a
              href={waLink(site, "barbearia")}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-gold text-black font-heading font-600 tracking-wide px-5 py-3.5 rounded-sm"
              data-testid="mobile-agendar-agora"
            >
              <CalendarCheck className="w-5 h-5" />
              AGENDAR AGORA
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
